/**
 * mtime-cache.ts — JAISIU-3496 / G5 shared primitive.
 *
 * An in-process cache keyed on `(key, mtimeMs)` so an "I've seen this version"
 * verdict stays warm until the underlying artifact changes. The mtime is
 * supplied by the caller on read — the cache never touches the filesystem
 * itself — which means the same primitive works for `fs.Stats.mtimeMs` from
 * `node:fs`, `updatedAt` epoch-ms from IndexedDB, or any other "version
 * identifier" the caller happens to have.
 *
 * Two places drive the design:
 *
 *   1. `src/agents/session-file-repair.ts` (JAISIU-chatswitch-fastpath +
 *      JAISIU-observability-idle-noise): the chat-history poll re-read the
 *      session file every ~6s. With dozens of open tabs that was the dominant
 *      cost of switching chats. Recording the clean verdict under
 *      `(path, mtimeMs)` lets the next poll short-circuit at the verdict
 *      check — a writer append advances mtime and naturally invalidates.
 *
 *   2. `ui/src/ui/storage/idb-session-cache.ts` (JAISIU-3474 boot path):
 *      the scroll-anchor loader reads the IDB record on every page load and
 *      runs a small "clamp/floor/trim" decode over the result. A second
 *      boot with the same IDB record is a cache hit and skips the decode.
 *
 * Semantics — see `MtimeCache` for the full contract. Highlights:
 *
 *   - `get(key, currentMtimeMs)` returns the cached value only when the
 *     stored mtime matches the caller-supplied `currentMtimeMs` AND the
 *     optional TTL has not expired. Otherwise it returns `undefined`.
 *   - `set` is a no-op when the entry already exists with the same
 *     `(key, mtimeMs)` and value (reference equality). This keeps hot
 *     write paths idempotent — a `chat.history` poll that re-records the
 *     same verdict does not churn the insertion-order eviction list.
 *   - `maxEntries` is enforced with simple FIFO eviction (insertion order).
 *     The spec accepts "LRU-ish, insertion order is fine" — proper LRU
 *     bookkeeping is future work if eviction churn becomes a hot spot.
 *   - In-process only. No persistence; no cross-process coordination.
 *
 * The class is generic over `V` because callers store different shapes
 * (`RepairReport`, `IdbScrollAnchorEntry`, …) and we don't want a base
 * class forcing a one-size-fits-none `unknown` value type.
 */
export interface MtimeCacheEntry<V> {
  readonly value: V;
  readonly mtimeMs: number;
  readonly storedAt: number;
}

export interface MtimeCacheOptions {
  /** Hard cap on the number of entries. Oldest (FIFO) is evicted first. Default 256. */
  maxEntries?: number;
  /** Time-to-live in milliseconds. Entries older than this are treated as misses. Default 60_000. */
  ttlMs?: number;
}

const DEFAULT_MAX_ENTRIES = 256;
const DEFAULT_TTL_MS = 60_000;

/**
 * `(key, mtimeMs)`-keyed in-process verdict cache. See the module header
 * for design notes.
 */
export class MtimeCache<V> {
  private readonly maxEntries: number;
  private readonly ttlMs: number;
  private readonly entries = new Map<string, MtimeCacheEntry<V>>();
  // JS `Map` preserves insertion order, so iterating `.keys()` yields the
  // oldest entry first — exactly what we want for FIFO eviction.
  private nowFn: () => number;

  constructor(opts?: MtimeCacheOptions, nowFn?: () => number) {
    this.maxEntries =
      typeof opts?.maxEntries === "number" && opts.maxEntries > 0
        ? Math.floor(opts.maxEntries)
        : DEFAULT_MAX_ENTRIES;
    this.ttlMs =
      typeof opts?.ttlMs === "number" && opts.ttlMs > 0
        ? Math.floor(opts.ttlMs)
        : DEFAULT_TTL_MS;
    // Test seam — vitest can swap in `vi.useFakeTimers().now` without
    // monkey-patching `Date.now` globally.
    this.nowFn = nowFn ?? Date.now;
  }

  /**
   * Returns the cached value for `key` if and only if the stored mtime
   * equals `currentMtimeMs` and the entry has not exceeded `ttlMs`.
   *
   * Returns `undefined` for any miss — including a TTL-expired entry. A
   * miss does NOT remove the expired entry; the next `set` call (or
   * `delete`) is responsible for cleanup. That mirrors the spec and keeps
   * `get` side-effect free.
   */
  get(key: string, currentMtimeMs: number): V | undefined {
    const entry = this.entries.get(key);
    if (!entry) return undefined;
    if (entry.mtimeMs !== currentMtimeMs) return undefined;
    const now = this.nowFn();
    if (now - entry.storedAt > this.ttlMs) return undefined;
    return entry.value;
  }

  /**
   * Records `value` for `key` at version `mtimeMs`. No-op if an existing
   * entry has the same `(key, mtimeMs)` and the same `value` reference.
   *
   * The reference-equality short-circuit matters for hot write paths
   * (e.g. `repairSessionFileIfNeeded` records a verdict every ~6s per
   * session): without it, the insertion-order eviction list would churn
   * every poll and the FIFO guarantee would be meaningless.
   */
  set(key: string, mtimeMs: number, value: V): void {
    const existing = this.entries.get(key);
    if (
      existing &&
      existing.mtimeMs === mtimeMs &&
      existing.value === value
    ) {
      return;
    }
    if (!existing && this.size() >= this.maxEntries) {
      // FIFO eviction: the first key returned by the Map iterator is the
      // oldest-inserted entry. `Map#keys().next().value` is the cheapest
      // way to grab it without allocating an array.
      const oldestKey = this.entries.keys().next().value;
      if (oldestKey !== undefined) {
        this.entries.delete(oldestKey);
      }
    }
    this.entries.set(key, {
      value,
      mtimeMs,
      storedAt: this.nowFn(),
    });
  }

  /**
   * Removes any cached entry for `key`. Safe to call when no entry
   * exists. Used by `repairSessionFileIfNeeded` on ENOENT so a
   * recreate-after-delete gets a fresh verdict.
   */
  delete(key: string): void {
    this.entries.delete(key);
  }

  /** Drops every entry. Exposed for test reset and for emergency GC. */
  clear(): void {
    this.entries.clear();
  }

  /** Current entry count. Useful for tests and metrics. */
  size(): number {
    return this.entries.size;
  }
}
